import { URL as SERVER_URL } from "./url";
import { generateIdempotencyKey } from "./idempotencyKey";

// The launches of this tab. A launch (a start or a resume) carries an id the browser makes, and every
// request on the session it opens names it (`launch_id`), so the server refuses the requests of a
// session another launch has closed (`SESSION_REPLACED`). The tab's own launch is the last one it sent,
// kept in `sessionStorage`, which lasts as long as the tab and survives its reloads and full-page
// navigations: each launch names it as `previous_launch_id`, so a reload, or a move to the next item,
// takes over the tab's own session without a question.

const OWN_LAUNCH_KEY = "ownLaunchId";
const REPLACE_CONSENT_KEY = "replaceLaunchConsent";

/** The page event a `SESSION_REPLACED` answer raises: `detail` names the request's launch
 * (`launchId`) and the session open now (`openSession`). */
export const SESSION_REPLACED_EVENT = "arrows:session-replaced";

// The launches this page has ended: nothing more is sent for them, and their late answers are dropped.
const endedLaunchIds = new Set();

export const newLaunchId = () => generateIdempotencyKey();

export function ownLaunchId() {
  try {
    return window.sessionStorage.getItem(OWN_LAUNCH_KEY);
  } catch (_) {
    return null;
  }
}

export function setOwnLaunchId(launchId) {
  try {
    if (launchId) window.sessionStorage.setItem(OWN_LAUNCH_KEY, launchId);
    else window.sessionStorage.removeItem(OWN_LAUNCH_KEY);
  } catch (_) {}
}

export function endLaunch(launchId) {
  if (launchId) endedLaunchIds.add(launchId);
}

export function isLaunchEnded(launchId) {
  return !!launchId && endedLaunchIds.has(launchId);
}

/** The launch a request names: `launch_id` in its body, or in its query (the step image). */
export function launchIdOf(config) {
  const data = config?.data;
  if (data && typeof data === "object" && !(typeof FormData !== "undefined" && data instanceof FormData)) {
    return data.launch_id || null;
  }
  if (typeof data === "string" && data.includes('"launch_id"')) {
    try {
      return JSON.parse(data).launch_id || null;
    } catch (_) {
      return null;
    }
  }
  const url = String(config?.url || "");
  if (!url.includes("launch_id=")) return null;
  try {
    return new window.URL(url, "http://localhost").searchParams.get("launch_id");
  } catch (_) {
    return null;
  }
}

/** A request cut off because its launch has ended: callers treat it as an abort. */
export const isCanceled = (error) => error?.code === "ERR_CANCELED" || error?.name === "CanceledError";

/**
 * Continue here, on the stopped popup: the reloaded page's launch of the same item closes the session
 * that replaced this one (its `launchId`) without asking again. One launch uses the consent, and only a
 * launch of that item acts on it.
 */
export function grantReplaceConsent({ launchId, kind, itemId }) {
  try {
    window.sessionStorage.setItem(
      REPLACE_CONSENT_KEY,
      JSON.stringify({ launchId, kind, itemId: String(itemId) })
    );
  } catch (_) {}
}

export function takeReplaceConsent({ kind, itemId }) {
  let consent = null;
  try {
    consent = JSON.parse(window.sessionStorage.getItem(REPLACE_CONSENT_KEY) || "null");
    window.sessionStorage.removeItem(REPLACE_CONSENT_KEY);
  } catch (_) {
    return null;
  }
  if (!consent || consent.kind !== kind || consent.itemId !== String(itemId)) return null;
  return consent.launchId || null;
}

/** The channel the tabs of one account share: a launch that closed a session announces it there. */
export const launchChannelName = (userId) => `arrows-launches-${userId}`;

/**
 * Tells the server the page stopped running `launchId` (`/session_leave/`). Nothing waits for it: a
 * `keepalive` fetch outlives the page that sends it, and it carries the sign-in token, as `sendBeacon`
 * cannot.
 */
export function sendLeave(launchId) {
  if (!launchId || typeof fetch !== "function") return;
  let token = null;
  try {
    token = window.localStorage.getItem("access_token");
  } catch (_) {}
  try {
    fetch(`${SERVER_URL}/api/session_leave/`, {
      method: "POST",
      keepalive: true,
      headers: {
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ launch_id: launchId }),
    }).catch(() => {});
  } catch (_) {}
}
